import { PackageStatus } from "@skygroup/shared/constants/packageStatus";
import {Flight} from "../ShoppingCart/interfaces";
import { packagePriceOption } from '@skygroup/shared/constants/checkoutInfo';
import { AgentAdditionalInfo } from "../ShoppingCart/agentActions/AgentActions";
import { DynamicPackageFlightsInfo } from "../../interfaces/flightInterface";

export interface PackageDataInterface {
    id: number;
    identifier?: string;
    package_cycle_id: number;
    package_type: string;
    related_packages: {name:string, id:number, url:string}[];
    dates: Dates;
    nights: number;
    auto_approve?: boolean;
    is_include_transfer: number;
    currency_sign: string;
    transfer_type: string;
    activities: Activities[];
    hotel: Hotel;
    flights: Flight[];
    destination: destination
    name: string;
    description: string;
    affiliate: null | affiliate;
    status:PackageStatus.STATUS_ACTIVE |
            PackageStatus.STATUS_ARCHIVED |
            PackageStatus.STATUS_DELETED |
            PackageStatus.STATUS_FULL |
            PackageStatus.STATUS_NOT_ACTIVE;
    show_optional_activities: boolean;
    settings:PackageSettingsInterface
    agent_additional_info:AgentAdditionalInfo
    eventTicket?: {
        event: any;
        ticket: any;
        quantity: number;
        deliveryMethod: any;
    };
    eventsManagedSeparately?: boolean;
}

export interface DynamicPackageDataInterface extends Omit<PackageDataInterface, 'flights'> {
    flights: DynamicPackageFlightsInfo;
    pax: {
        adult: number;
        child: number;
        infant: number;
    }
    /** Carried from the flight view response next to flights + pax (only when
     *  true); see ShoppingCart/userAgreements.ts. */
    is_israel_law_enabled?: boolean;
    /** Native hotel room currency (distinct from `currency_sign` when mixed → USD). */
    hotel_currency_sign?: string;
    /** Native flight currency. */
    flight_currency_sign?: string;
    eventTicket?: {
        event: any;
        ticket: any;
        quantity: number;
        deliveryMethod: any;
    }
}

export interface CheapestFlightInterface {
    identifier: string;
    flightFrom: Flight[];
    flightBack: Flight[];
    numOfStops: {
        flightFrom: number
        flightBack: number
    },
    price: {
        adult: number;
        currency_sing: string;
        grandTotal: number;
    },
    status: string;
}

export interface PackageSettingsInterface {
    checkout_info: string;
    promo_text?: string;
    hide_flight_from?: boolean;
    hide_flight_back?: boolean;
    flight_only?: boolean;
    info_box?: any;
    activities_title: string;
    activities_semi_title: string;
    price_option_type: packagePriceOption;
    show_affiliate_info: boolean;
    show_infants: boolean;
    show_cancellation_terms: boolean;
    show_coupon_code: boolean;
    show_guest_birthday: boolean;
    show_guest_passport: boolean;
    is_guest_passport_required: boolean;
    subsidize_conf:SubsidizeConfInterface
    infant_price:number
    show_guest_passport_attachment:boolean
    is_required_guest_passport_attachment:boolean
    max_optional_activities:number
    show_optional_activities:boolean
    flight_comment: any,
    rooms_comment: any,
    is_customer_email_required: boolean;
    show_order_comment: boolean;
    guest_icon_to_override_by_text: string;
    manage_order_back_image: string;
    show_flights_cart: boolean;
    show_terms: boolean;
    terms_text: string;
    terms_link: string;
    guests_extra_fields: ExtraField[];
    order_extra_fields: ExtraField[];
    show_room_filters: boolean;
    limit_cart_max_rooms:number
    allow_only_guest_from_subsidize:boolean
    package_info_as_schedule:boolean
    show_agency_id:boolean
    show_docket_id:boolean
    agencies:Record<string | number, string>
    redirect?: string,
    popup_title?: string,
    popup_content?: string,
}
interface ExtraField {
    type: string;
    name: string;
    label: string;
    is_required: boolean;
    max_length?: string;
  }
export interface SubsidizeConfInterface {
    currency: string;
    currency_sign:string;
    title: string;
    btnTitle: string;
    groups: { discount: number; group_id: string; type: string }[];
    validateBy: string[]
}

export interface affiliate {
    name: string
    logo: string
}
export interface destination {
    id: string
    name: string
    dest_info_file: string
}

export interface Dates {
    start: number;
    end: number;
}

export interface Activities {
    id: number;
    name: string;
    image: string;
}

export interface ImageInterface {
    id: number;
    thumbnail: string;
    is_main_image: number;
    meta_data: string;
    description: string;
}

export type ItineraryItem = {
    day: string;
    city: string | null;
    cityName: string | null;
    boardBasis: string;
    addService: string | null;
    description: string;
    remark: string | null;
    image?: string;
};

export type TourInfo = {
    country: string;
    countryName: string;
    link: string;
    supplier: string;
    title: string;
    days: string;
    code: string;
    lang: string;
    type: string | null;
    guaranty: string;
    nights: string;
    description: string;
    brochure: string | null;
    img: string;
};

export type OrgTourDataInterface = {
    itinerary: ItineraryItem[];
    tourInfo: TourInfo;
};


export interface Hotel {
    id: number;
    identifiers: string;
    name: string;
    stars: number;
    mainImage: string;
    images: ImageInterface[];
    description: string | { paragraphs: string[]; title: string }[];
    youtube: string;
    address: string;
    properties: string[];
    facilities: any[];
    maxChildAge: number;
    small_desc?: string;
    area?: any;
    website?: any;
    org_tour_data?: OrgTourDataInterface;
    rooms: any[];
    location?: {
      cityName: string;
      countryCode: string;
      countryName: string;
      iataCode: string;
      id: number;
      languageCode: string;
      lat: number;
      long: number;
      tenantId: number;
      type: string;
    };
    additional_info?: any;
  }
export interface Data {
    id: number;
    package_cycle_id: number;
    package_type: PackageTypes;
    dates: Dates;
    nights: number;
    is_include_transfer: number;
    activities: Activities[];
    hotel: Hotel;
}

export enum PackageTypes {
    HOTEL_ONLY = "hotel_only",
    FULL_PACKAGE = "full_package",
    DYNAMIC_PACKAGE = "dynamic_package",
    ACTIVITIES = "activities",
    ORG_TOUR = 'org_tour',
}
export enum PackageDetailsMode {
    PACKAGE = "package",
    DYNAMIC_PACKAGE = "dynamicPackage",
    HOTEL = "hotel",
}

// ##########################

export interface packageFormattedDataInterface {
    dates: string
    weekDays: string
    nights?: string
}

